(ns nosco.views.userlevel.ideas-shared
  (:require
   ["nosco-objectid/nosco-objectid" :as ObjectId]
   [clojure.string :as string]
   [goog.string :as gstring]
   [goog.string.format]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.controllers :refer [has-flag?]]
   [nosco.graphql-client :as gql]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.router :as router]
   [nosco.shared.form-fields :as form-fields]
   [nosco.ui.animations :as animations]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.checkbox :as checkbox]
   [nosco.ui.dropdown3 :as dropdown3]
   [nosco.ui.dropdowns2 :as dropdowns2]
   [nosco.ui.empty-states :as empty-states]
   [nosco.ui.errors :as errors]
   [nosco.ui.inputs :as inputs]
   [nosco.ui.layouts :as layouts]
   [nosco.ui.loading :as loading]
   [nosco.ui.modals3 :as modals3]
   [nosco.ui.select2 :as select2]
   [nosco.ui.tabs :as tabs]
   [nosco.ui.text :as text]
   [nosco.ui.toast :as toast]
   [nosco.ui.user-menubar :as menubar]
   [nosco.util :as util]
   [nosco.util.filters-url :as filters-url]
   [nosco.views.userlevel.idea-card :as idea-card]
   [nosco.views.userlevel.ideas-filters :as ideas-filters]
   [nosco.views.userlevel.ideas-table :as ideas-table]
   [nosco.views.userlevel.list-protocols :as list-protocols]
   [nosco.views.userlevel.lists-shared :as lists-shared]
   [react :as React]
   [reseda.react :as rr]
   [nosco.ui.tooltips :as tooltips]))

(def IdeaListStoreContext (React/createContext))

;; White listing a few selected order fields for now, for security and being explicit
(defn query->order-by [query-params]
  (let [v (:order-by query-params)
        [field order] (string/split (str v) #":" 2)
        field (get {"published-at"    :PUBLISHED_AT
                    "updated-at"      :UPDATED_AT
                    "edited-at"       :EDITED_AT
                    "days-in-stage"   :LAST_STAGE_CHANGE_AT
                    "stage-position"  :STAGE_POSITION
                    "stage-title"     :STAGE_TITLE
                    "status-position" :STATUS_POSITION
                    "status-title"    :STATUS_TITLE
                    "author"          :AUTHOR_NAME
                    "update-required" :STAGE_STATE
                    "comments"        :COMMENTS_COUNT
                    "likes"           :LIKES_COUNT
                    "title"           :TITLE} field :LAST_STAGE_CHANGE_AT)
        order (get {"asc" :ASC "desc" :DESC} order :DESC)]
    {:field field :order order}))

(defn order-by->str [{:keys [field order]}]
  (when-let [k (get {:PUBLISHED_AT    "published-at"
                     :UPDATED_AT      "updated-at"
                     :EDITED_AT       "edited-at"
                     :LAST_STAGE_CHANGE_AT "days-in-stage"
                     :STAGE_POSITION  "stage-position"
                     :STAGE_TITLE     "stage-title"
                     :STATUS_POSITION "status-position"
                     :STATUS_TITLE    "status-title"
                     :AUTHOR_NAME     "author"
                     :STAGE_STATE     "update-required"
                     :COMMENTS_COUNT  "comments"
                     :LIKES_COUNT     "likes"
                     :TITLE           "title"} field)]
    (str k ":" (case order :ASC "asc" :DESC "desc"))))

(defn query-params-state
  "The list state that the browser (URL + popstate) actually carries."
  []
  (let [query-params (lists-shared/get-search-params-map)
        order-by (:order-by query-params)
        custom-filters (seq (filters-url/query-params->ideas-filters query-params))
        active-variant (lists-shared/query->active-variant query-params)
        columns (when (util/non-blank (:columns query-params))
                  (ideas-table/query->selected-columns (:columns query-params)))]
    (cond->
     {:search-string (lists-shared/query->search-string query-params)
      :pagination {:first (let [popstate-n (lists-shared/peek-pagination)
                                url-n (lists-shared/query->pagination-first query-params)]
                            (or popstate-n url-n))}}

      custom-filters
      (assoc :custom-filters (vec custom-filters))

      order-by
      (assoc :orderBy (query->order-by query-params))

      columns
      (assoc :selected-columns columns)

      active-variant
      (assoc :active-variant active-variant))))

(defn use-ideas-store [selector]
  (let [{:keys [store]} (hooks/useContext IdeaListStoreContext)]
    (rr/useStore store selector)))

(defn get-stored-persistent-state [persistent-state-prefix]
  (let [variant (some-> (.getItem js/localStorage (str persistent-state-prefix "/variant")) keyword)]
    (cond-> {}
      variant (assoc :active-variant variant))))

(defn use-active-variant []
  (let [mobile (util/use-is-mobile?)
        tablet (util/use-is-tablet?)
        variant* (use-ideas-store :active-variant)
        variant* (or variant* :list)
        variant
        (cond
          mobile :card
          (and tablet (not (#{:card :list} variant*))) :card
          :else variant*)]
    (if (= :card variant)
      :masonry
      variant)))

(defnc SaveViewModal [{:keys [view-state-api showing editing-view on-close]}]
  (let [[title set-title] (hooks/useState "")
        [public set-public] (hooks/useState false)
        [default set-default] (hooks/useState false)
        [loading set-loading] (hooks/useState false)
        can-save-public (list-protocols/can-save-public view-state-api)
        save-existing? editing-view
        save-existing-view (fn []
                             (let [view-with-props
                                   (-> editing-view
                                       (assoc
                                        :public public
                                        :default default
                                        :title title))]

                               (set-loading true)
                               (-> (list-protocols/save-view view-state-api view-with-props)
                                   (.then (fn []
                                            (set-loading false)
                                            (on-close)
                                            (list-protocols/set-view view-state-api view-with-props {}))))))
        save-new-view (fn []
                        (let [view-with-props
                              (-> (list-protocols/active-view view-state-api)
                                  (dissoc :readonly)
                                  (assoc
                                   :public public
                                   :default default
                                   :title title
                                   :id (str (ObjectId.))))]

                          (set-loading true)
                          (-> (list-protocols/save-view view-state-api (update view-with-props :props #(merge {:active-variant :list} %)))
                              (.then (fn []
                                       (set-loading false)
                                       (on-close)
                                       (list-protocols/set-view view-state-api view-with-props {}))))))
        private-views-enabled? false ;; coming soon
        ]

    (hooks/useEffect (fn []
                       (let [{:keys [title public default]}
                             (if (nil? editing-view)
                               {:public true}
                               editing-view)]
                         (set-title (str title))
                         (set-public public)
                         (set-default (boolean default))))
                     [editing-view])
    [modals3/ModalDialog {:showing showing
                          :on-close on-close
                          :size :narrow
                          :title  (if save-existing?
                                    (t/t "Edit title & Settings")
                                    (t/t "Save view as..."))
                          :cancel-label (t/t "Cancel")
                          :confirm-label (t/t "Save")
                          :confirm-props {:loading loading
                                          :disabled (string/blank? title)}
                          :on-confirm (fn []
                                        (if save-existing?
                                          (save-existing-view)
                                          (save-new-view)))}

     [inputs/FormField {:label (t/t "Title")
                        :required true}
      [layouts/Column {:gap "8px"}
       [inputs/TextInput {:value title
                          :on-change (fn [e]
                                       (set-title (-> e .-target .-value)))}]

       (when can-save-public
         [checkbox/CheckboxWithLabel {:label
                                      [tooltips/WithTooltip {:label (t/t "Private views are coming soon")}
                                       [layouts/Row {:gap "4px" :class "align-items-center"}
                                        [icons2/Icon {:icon icons2/UserMultiple-small

                                                      :color (if private-views-enabled? colors/neutral-500 colors/neutral-100)}]
                                        (t/t "Public")]]
                                      :disabled (not private-views-enabled?)
                                      :checked true
                                      :on-change (fn [e]
                                                   (set-public not))}])
       [checkbox/CheckboxWithLabel {:label [layouts/Row {:gap "4px" :class "align-items-center"}
                                            [icons2/Icon {:icon icons2/Star-small
                                                          :color colors/neutral-500}]
                                            (t/t "Set as default")]
                                    :checked default
                                    :on-change (fn [e]
                                                 (set-default not))}]]]]))

(def ideas-query (gql/graphql-query "./Ideas.graphql" :SearchIdeas))

(defn search-filter->stage-id [search-filter]
  (when (= (:ideas.filter/dimension search-filter) :stage)
    (:ideas.filter/value search-filter)))

;; Sort order used when no other sort order is selected AND when the search string is nil
(def ^:const default-sorting {:field :LAST_STAGE_CHANGE_AT :order :DESC})
(def ^:const default-sorting-all-stages {:field :PUBLISHED_AT :order :DESC})

(defn idea-fetch-vars [state]
  (let [vars (select-keys @state [:pagination :orderBy :search])
        active-variant (:active-variant @state)
        search-string (util/non-blank (:search-string @state))
        custom-filters (:custom-filters @state)
        stage-filter (some search-filter->stage-id (:search @state))
        ;; When a sort order is selected, use it
        ;; When no sort order is selected AND the search string is nil, use a default sort order
        ;; When no sort order is selected AND the search string is NOT nil, use the "search relevance" sort order
        order-by (:orderBy @state)
        default-order-by? (and (empty? search-string) (nil? order-by))
        vars (cond-> vars
               (util/non-blank search-string)
               (update :search conj {:ideas.filter/dimension :form-submission
                                     :ideas.filter/operator :search
                                     :ideas.filter/value search-string})

               (seq custom-filters)
               (update :search concat custom-filters)

               (= active-variant :table)
               (assoc :includeContent true)
               default-order-by?
               (assoc :orderBy (if stage-filter default-sorting default-sorting-all-stages)))
        vars (update vars :search #(mapv pr-str %))]
    vars))

(defn- fetch-ideas [state]
  (let [vars (idea-fetch-vars state)
        r (gql/query-promise! ideas-query vars)]
    (-> r
        (.then #(get-in % [:data :searchIdeas3]))
        (.catch #(toast/show-message-error {:title (t/t "Error while fetching ideas")})))))

(defn- set-ideas! [state ideas-p]
  (swap! state assoc :search-ideas* (errors/suspending-value ideas-p)))

(defn fetch-and-set-ideas! [state]
  (let [ideas-p (util/delay-atleast (fetch-ideas state) 300)]
    (set-ideas! state ideas-p)))

(defn use-get-fetch! [debounce]
  (let [{:keys [state]} (hooks/useContext IdeaListStoreContext)
        fetch-ideas (if debounce
                      (util/use-debounce debounce #(fetch-ideas state))
                      #(fetch-ideas state))]
    #(set-ideas! state (fetch-ideas))))

(defn make-legacy-view-state [{:keys [state persistent-state-prefix]}]
  (reify list-protocols/ListViewState
    (views-layouts-active? [_] false)
    (get-views [_ _])
    (set-view [_ _view])
    (save-view [_ _view])
    (save-view-as [_ _view])
    (delete-view [_ _view])
    (active-view [_])
    (can-save-public [_])
    (get-state-from-browser [_]
      (merge
       (get-stored-persistent-state persistent-state-prefix)
       (query-params-state)))

    (on-stage-change [_ _]
      (fetch-and-set-ideas! state))

    (initial-state [_ _]
      {:pagination {:first 24}
       :active-variant :list
       :search-string nil
       :search-ideas* (rr/suspending-forever)
       :custom-filters []
       :selected-columns (ideas-table/default-table-columns)
       :orderBy nil})

    (set-order-by [_ order-by]
      (router/update-query-params-silently! "order-by" (order-by->str order-by))
      (swap! state assoc :orderBy order-by))
    (set-layout [_ layout]

      (router/update-query-params-silently! "variant" (name layout))
      (.setItem js/localStorage (str persistent-state-prefix "/variant") (name layout))
      (swap! state assoc :active-variant layout))

    (set-filters [_ new-filters]

      (router/update-query-params-silently! "filter" (filters-url/serialize-filters new-filters))
      (swap! state assoc :custom-filters new-filters))

    (set-search [_ s]
      ;; Searching sorts by relevance (`idea-fetch-vars` only sends `orderBy`
      ;; when it is set), so an explicit sort is dropped when a search starts,
      ;; and again when it is cleared — a sort picked while searching does not
      ;; outlive the search. With no sort, the contextual default applies.
      (let [searching-before? (some? (util/non-blank (:search-string @state)))
            searching-now? (some? (util/non-blank s))]
        (router/update-query-params-silently! "search" (util/non-blank s))
        (when (not= searching-before? searching-now?)
          (router/update-query-params-silently! "order-by" nil)
          (swap! state assoc :orderBy nil))
        (swap! state assoc :search-string s)))

    (show-more [_]
      (swap! state update-in [:pagination :first] #(+ % 24))
      ;; we decided not to reflect the pagination in the URL for now.
      (lists-shared/push-pagination! (get-in @state [:pagination :first])))

    (set-selected-fields [_ cs]

      (router/update-query-params-silently! "columns" (ideas-table/columns->str cs))
      (swap! state assoc :selected-columns cs))))

(defn sort-field-options
  "Returns available sort field options (without order).
   When a stage is provided, uses its ideaTemplate.
   When master-form is provided (e.g. on 'All posts' tab), uses that instead.
   When ideabox is provided:
     - Uses STAGE_POSITION instead of STAGE_TITLE (stages have inherent order)
     - Uses STATUS_POSITION instead of STATUS_TITLE if ideabox has local statuses"
  [{:keys [stage master-form ideabox]}]
  (let [has-local-statuses? (seq (:localStatuses ideabox))
        in-ideabox-context? (some? ideabox)
        base-options
        [{:label (t/t "Title") :field :TITLE}
         {:label (t/t "Publish date") :field :PUBLISHED_AT}
         {:label (t/t "Modified date") :field :EDITED_AT}
         {:label (t/t "In stage since") :field :LAST_STAGE_CHANGE_AT}
         {:label (if in-ideabox-context? (t/t "Stage") (t/t "Stage title"))
          :field (if in-ideabox-context? :STAGE_POSITION :STAGE_TITLE)}
         {:label (if has-local-statuses? (t/t "Status") (t/t "Status title"))
          :field (if has-local-statuses? :STATUS_POSITION :STATUS_TITLE)}
         {:label (t/t "Author") :field :AUTHOR_NAME}
         {:label (t/t "Update required") :field :STAGE_STATE}
         {:label (t/t "Comments") :field :COMMENTS_COUNT}
         {:label (t/t "Likes") :field :LIKES_COUNT}]
        ;; Use stage's ideaTemplate if available, otherwise fall back to master-form
        template-fields (or (:ideaTemplate stage) master-form)]
    (if (and (has-flag? :form-submission-table) template-fields)
      (into base-options
            (for [field template-fields
                  :let [field-type (keyword (:type field))]
                  :when (not= :TITLE field-type)]
              {:label (:title field)
               :icon (icons2/->small (form-fields/field-type-icon field-type))
               :field :SUBMISSION_FORM_FIELD
               :subfield (:id field)}))
      base-options)))

(defn- sort-by-matches-field?
  "Check if a sort-by value matches a field option (ignoring order)."
  [sort-by field-option]
  (and (= (:field sort-by) (:field field-option))
       (= (:subfield sort-by) (:subfield field-option))))

(defn- get-sort-label
  "Get the label for the current sort from field options."
  [active-sort-by field-options]
  (when active-sort-by
    (->> field-options
         (filter #(sort-by-matches-field? active-sort-by %))
         first
         :label)))

(defn- sort-is-default?
  "Check if the sort matches the contextual default (should not show pill/activation)."
  [active-sort-by stage]
  (let [contextual-default (if stage default-sorting default-sorting-all-stages)]
    (= (select-keys active-sort-by [:field :order])
       contextual-default)))

(defnc SortMenuInner [{:keys [close field-options active-sort-by on-sort-by]}]
  (let [current-order (or (:order active-sort-by) :DESC)
        current-field (when active-sort-by
                        (select-keys active-sort-by [:field :subfield]))
        on-order-change (fn [order]
                          (close)
                          (if current-field
                            (on-sort-by (assoc current-field :order order))
                            ;; Default to first field if none selected
                            (on-sort-by {:field :EDITED_AT :order order})))
        on-field-change (fn [field-option]
                          (close)
                          (on-sort-by (assoc (select-keys field-option [:field :subfield])
                                             :order current-order)))
        ;; Split into base fields and form fields
        base-fields (remove #(= :SUBMISSION_FORM_FIELD (:field %)) field-options)
        form-fields (filter #(= :SUBMISSION_FORM_FIELD (:field %)) field-options)]
    [dropdown3/DropdownMenu {:style {:width "220px"
                                     :--dropdown3-item-list-max-height "26rem"}}
     [dropdown3/DropdownItemList
      [dropdown3/DropdownItemGroup {:title (t/t "Order")}
       [dropdown3/DropdownItem {:text (t/t "Ascending")
                                :selected (= current-order :ASC)
                                :on-click (fn [e]
                                            (.preventDefault e)
                                            (.stopPropagation e)
                                            (on-order-change :ASC))}]
       [dropdown3/DropdownItem {:text (t/t "Descending")
                                :selected (= current-order :DESC)
                                :on-click (fn [e]
                                            (.preventDefault e)
                                            (.stopPropagation e)
                                            (on-order-change :DESC))}]]
      [dropdown3/DropdownItemGroup {:title (t/t "Field") :separator true}
       (for [field-option base-fields]
         [dropdown3/DropdownItem {:text (:label field-option)
                                  :title (:label field-option)
                                  :icon (:icon field-option)
                                  :selected (sort-by-matches-field? active-sort-by field-option)
                                  :on-click (fn [e]
                                              (.preventDefault e)
                                              (.stopPropagation e)
                                              (on-field-change field-option))}])]
      (when (seq form-fields)
        [dropdown3/DropdownItemGroup {:title (t/t "Form fields") :separator true}
         (for [field-option form-fields]
           [dropdown3/DropdownItem {:text (:label field-option)
                                    :title (:label field-option)
                                    :icon (:icon field-option)
                                    :selected (sort-by-matches-field? active-sort-by field-option)
                                    :on-click (fn [e]
                                                (.preventDefault e)
                                                (.stopPropagation e)
                                                (on-field-change field-option))}])])]]))

(defnc SearchInput [{:keys [mobile draft-mode loading on-search on-filter-click filter-activated embed-filter?]
                     :or {embed-filter? true}}]
  (let [search-string (use-ideas-store :search-string)
        input (hx/f [inputs/SearchInput
                     {:placeholder (t/t "Search ideas...")
                      :style (when-not mobile {:min-width "400px"})
                      :loading loading
                      :auto-focus (contains? (router/current-route-query-params)
                                             :focus-search)
                      :value (str search-string)
                      :on-change
                      (fn [^js e]
                        (on-search (-> e .-target .-value)))}])]
    (if (or draft-mode mobile (not embed-filter?))
      input
      [inputs/SearchInputWithButton
       {:button (hx/f [buttons2/Button
                       {:variant :action
                        :icon icons2/Filter-small
                        :label (t/t "Filter")
                        :activated filter-activated
                        :on-click on-filter-click}])
        :input input}])))

(defnc SortMenuMobile [{:keys [menu-inner active-sort-by on-clear-sort]}]
  (let [[open set-open] (hooks/useState false)
        close #(set-open false)
        activated (some? active-sort-by)]
    [:<>
     [buttons2/Button {:variant :icon-light
                       :on-click #(set-open true)
                       :icon icons2/Order-large
                       :activated activated}]
     [modals3/MobileModal
      {:open open
       :on-close close}
      [modals3/MobileModalHeader {:on-close close}
       [modals3/MobileModalHeaderTitle {:title (t/t "Sort ideas")}]]
      (React/cloneElement menu-inner #js {:close close})
      (when activated
        [modals3/MobileModalFooter
         [layouts/Row {:class "align-items-center justify-content-center"}
          [:div {:class "p-spacing-16"}
           [buttons2/Button {:variant :text
                             :label (t/t "Clear sort order")
                             :on-click (fn []
                                         (close)
                                         (on-clear-sort))}]]]])]]))

(defnc SortMenuDesktop [{:keys [menu-inner on-clear-sort icon-only? changed? show-as-active?]}]
  [:<>
   (when (and show-as-active? (not icon-only?))
     [buttons2/Button {:variant :text
                       :label (t/t "Clear sort")
                       :size :xsmall
                       :on-click (fn [] (on-clear-sort))}])
   [dropdowns2/Dropdown
    {:button (hx/f
              [buttons2/Button (cond-> {:variant (if icon-only? buttons2/NewDefault :action-secondary)
                                        :size (if icon-only? buttons2/SizeSmall :default)
                                        :tone (when (and icon-only? show-as-active?) buttons2/ToneActive)
                                        :dot-color (when (and icon-only? changed?) colors/error-600)
                                        :activated (when-not icon-only? show-as-active?)
                                        :icon icons2/Order-small}
                                 (not icon-only?)
                                 (assoc :label (t/t "Sort")
                                        :truncate true
                                        :class "ideas-list-header-sort-button"))])
     :tooltip (when icon-only? (t/t "Sort"))
     :popper-options {:offset [0 8]
                      :placement "bottom-end"}}
    (fn [{:keys [close]}]
      (React/cloneElement menu-inner #js {:close close}))]])

(defnc SortMenu [{:keys [mobile? active-sort-by on-sort-by stage ideabox icon-only? changed?]}]
  (let [;; When on "All posts" tab (no stage), use the master submission form from ideabox
        master-form (when-not stage (:masterSubmissionForm ideabox))
        field-options (sort-field-options {:stage stage :master-form master-form :ideabox ideabox})
        search-string (util/non-blank (use-ideas-store :search-string))
        contextual-default (if stage default-sorting default-sorting-all-stages)
        active-or-default-sort-by (cond active-sort-by active-sort-by
                                        (empty? search-string) contextual-default
                                        :else nil)
        ;; While searching, clearing the sort means relevance. Otherwise it means
        ;; the contextual default, which is set explicitly rather than as a nil
        ;; sort: the two fetch identically, but only the explicit one can be told
        ;; apart from the active view's saved sort in the URL.
        on-clear-sort #(on-sort-by (when-not search-string contextual-default))
        ;; Don't show as activated if it matches the contextual default
        show-as-active? (and active-sort-by (not (sort-is-default? active-sort-by stage)))
        menu-inner (hx/f [SortMenuInner {:field-options field-options
                                         :active-sort-by active-or-default-sort-by
                                         :on-sort-by on-sort-by}])]
    (if mobile?
      [SortMenuMobile {:menu-inner menu-inner
                       :active-sort-by active-sort-by
                       :on-clear-sort on-clear-sort}]
      [SortMenuDesktop {:show-as-active? show-as-active?
                        :icon-only? icon-only?
                        :changed? changed?
                        :on-clear-sort on-clear-sort
                        :menu-inner menu-inner}])))

(defnc StagesTabs [{:keys [ideabox active-stage-id on-stage-change]}]
  (let [tablet (util/use-is-tablet?)]
    [tabs/TabGroup
     {:tabs (into [{:key ::all
                    :label (t/t "All ideas")
                    :width "auto"}]
                  (map (fn [stage]
                         {:key (:id stage)
                          :label (:title stage)
                          :icon (when (:hasEvaluationTemplate stage) icons2/Evaluate-small)
                          :badge (:ideasCount stage)
                          :tooltip true}))
                  (:stages ideabox))
      :selected-tab active-stage-id
      :visible-count (if tablet 3 6)
      :background-variant :white
      :on-select
      (fn [k]
        (on-stage-change k))}]))

(defnc StageSelect [{:keys [ideabox active-stage-id on-stage-change total-count]}]
  [select2/NativeSelect
   {:value active-stage-id
    ;; iOS Safari does not respect the hidden attribute on options
    :placeholder (t/t "Select a stage...")
    :on-change
    (fn [k]
      (on-stage-change k))}
   [select2/Option
    {:label (t/t "All ideas (%s)" (util/number-format total-count))
     :value ::all}]
   (for [stage (:stages ideabox)]
     [select2/Option
      {:label (gstring/format "%s (%s)" (:title stage) (:ideasCount stage))
       :value (:id stage)}])])

(def ^:private delete-my-drafts-mutation
  (gql/graphql-query "./DeleteAllDrafts.graphql" :DeleteAllDrafts))

(defnc DeleteAllDraftsConfirmationModal [{:keys [showing on-close ideas-count on-success]}]
  (let [[loading set-loading] (hooks/useState false)]

    [modals3/ModalDialog {:title (t/t "Delete all drafts?")
                          :variant :destructive
                          :loading loading
                          :showing showing
                          :on-close on-close
                          :cancel-label (t/t "Cancel")
                          :on-confirm (fn []
                                        (set-loading true)
                                        (-> (gql/mutation-promise! delete-my-drafts-mutation {})
                                            (.then
                                             (fn [r]
                                               (when on-success (on-success))
                                               (let [deleted-count (get-in r [:data :deleteMyDrafts :totalCount])]
                                                 (toast/show-confirmation {:title (t/tp ["%s draft deleted"
                                                                                         "%s drafts deleted"] deleted-count deleted-count)}))
                                               (on-close)))
                                            (.catch
                                             (fn [e]
                                               (errors/show-error-toast! {:text (t/t "An error occurred while deleting drafts")})
                                               (errors/maybe-handle-sentry e)))
                                            (.finally (fn []

                                                        (set-loading false)))))

                          :confirm-label (t/t "Delete")}
     [:<>
      [text/Text {:variant text/Body-Md-Regular}
       (t/tp-html
        ["Delete <strong>%s</strong> draft?"
         "Delete <strong>%s</strong> drafts?"]
        ideas-count
        ideas-count)]
      " "
      [text/Text {:variant text/Body-Md-Regular}
       (t/t "This can't be undone.")]]]))

(defnc ListHeader
  [{:keys [views-layouts-active
           count
           draft-mode
           set-filter-open
           active-sort-by
           on-sort-by
           active-variant
           on-active-variant
           on-edit-columns
           loading
           filter-active
           on-search
           mobile
           list-variants
           title
           pre-header
           custom-filters
           on-change-filters
           ideabox
           active-stage
           active-stage-id
           my-ideas-tabs
           on-stage-change
           fetch-ideas!

           saved-views
           active-view
           on-change-view

           on-save-view
           on-save-as-view
           on-delete-view

           set-showing-delete-drafts]}
   ref]

  {:wrap [(React/forwardRef)]}
  (let [[filter-pills-data* _loading] (rr/useCachedSuspending (use-ideas-store :filter-pills-data*))
        searching? (some? (util/non-blank (use-ideas-store :search-string)))
        has-stages (and ideabox (> (clojure.core/count (:stages ideabox)) 1))
        mobile-showing (hooks/useContext menubar/ScrollDirectionShowingContext)
        stages-select-ref (React/useRef)
        stages-select-bounds (animations/use-measure stages-select-ref)]
    [:<>
     pre-header
     (when-not mobile
       [lists-shared/HeaderTitle {:count count
                                  :mobile mobile
                                  :title title
                                  :ideabox ideabox
                                  :right-button (when (and draft-mode (> count 0))
                                                  (hx/f [buttons2/Button
                                                         {:variant buttons2/NewDefault
                                                          :icon icons2/Delete-small
                                                          :label (t/t "Delete all")
                                                          :on-click #(set-showing-delete-drafts true)}]))}])
     my-ideas-tabs
     (when has-stages
       (if mobile
         [:div {:ref stages-select-ref
                :class ["ideas-list-header__stages"
                        (when mobile-showing "ideas-list-header__stages--mobile-showing")]}
          [StageSelect
           {:ideabox ideabox
            :active-stage-id active-stage-id
            :on-stage-change on-stage-change
            :total-count count}]]
         [:div {:class ["ideas-list-header__stages"]
                :ref stages-select-ref}
          [StagesTabs
           {:ideabox ideabox
            :active-stage-id active-stage-id
            :on-stage-change on-stage-change}]]))
     ;; Only render when the pills are ready to render. We want to avoid having a loading state here,
     ;; so the whole page must suspend when first loading and then use the cached data on subsequent
     ;; renders so that the pills always look correct without visibly loading.
     (if views-layouts-active
       (let [default-sort (if active-stage default-sorting default-sorting-all-stages)]
         [lists-shared/ListHeaderControlsWithViews {:mobile mobile
                                                    :sticky-offset (when ideabox (:height stages-select-bounds))
                                                    :search-input
                                                    (hx/f [SearchInput {:mobile mobile
                                                                        :draft-mode draft-mode
                                                                        :loading loading
                                                                        :on-search on-search
                                                                        :on-filter-click #(set-filter-open not)
                                                                        :filter-activated filter-active
                                                                        :embed-filter? false}])
                                                    :draft-mode draft-mode
                                                    :loading loading
                                                    :set-filter-open set-filter-open
                                                    :filter-active filter-active
                                                    :on-filter-click #(set-filter-open not)
                                                    :default-sort default-sort
                                                    :sort-menu-fn
                                                    (fn [mobile changed?]
                                                      (hx/f [SortMenu {:mobile? mobile
                                                                       :on-sort-by on-sort-by
                                                                       :active-sort-by active-sort-by
                                                                       :stage active-stage
                                                                       :ideabox ideabox
                                                                       :icon-only? true
                                                                       :changed? changed?}]))
                                                    :active-variant active-variant
                                                    :on-edit-columns on-edit-columns
                                                    :list-variants list-variants
                                                    :on-active-variant on-active-variant

                                                    :saved-views saved-views
                                                    :active-view active-view
                                                    :on-change-view on-change-view

                                                    :on-save-view on-save-view
                                                    :on-save-as-view on-save-as-view
                                                    :on-delete-view on-delete-view

                                                    :ref ref}
          (when-not mobile
            (let [filter-pills (ideas-filters/idea-filter-pills custom-filters
                                                                (rr/deref* filter-pills-data*)
                                                                (fn [filters]
                                                                  (on-change-filters filters)
                                                                  (fetch-ideas!))
                                                                (fn [{:ideas.filter/keys [dimension attr]}]
                                                                  (set-filter-open [dimension attr])))
                  master-form (when-not active-stage (:masterSubmissionForm ideabox))
                  field-options (sort-field-options {:stage active-stage :master-form master-form :ideabox ideabox})
                  ;; Don't show sort pill if it matches the contextual default
                  show-sort-pill? (and active-sort-by (not (sort-is-default? active-sort-by active-stage)))
                  sort-label (when show-sort-pill? (get-sort-label active-sort-by field-options))
                  sort-pill (when sort-label
                              (hx/f [lists-shared/SortPill {:label sort-label
                                                            :order (:order active-sort-by)
                                                            ;; While searching, clearing the sort
                                                            ;; means relevance, not the default sort
                                                            :on-clear #(on-sort-by (when-not searching? default-sort))}]))]
              (when (or sort-pill (seq filter-pills))
                [lists-shared/FilterPills {:sort-pill sort-pill
                                           :pills filter-pills}])))])
       [lists-shared/ListHeaderControls {:mobile mobile
                                         :sticky-offset (when ideabox (:height stages-select-bounds))
                                         :search-input
                                         (hx/f [SearchInput {:mobile mobile
                                                             :draft-mode draft-mode
                                                             :loading loading
                                                             :on-search on-search
                                                             :on-filter-click #(set-filter-open not)
                                                             :filter-activated filter-active
                                                             :embed-filter? false}])
                                         :draft-mode draft-mode
                                         :loading loading
                                         :set-filter-open (when-not draft-mode set-filter-open)
                                         :on-filter-click (when-not draft-mode #(set-filter-open not))
                                         :filter-active filter-active
                                         :sort-menu-fn
                                         (fn [mobile _changed?]
                                           (hx/f [SortMenu {:mobile? mobile
                                                            :on-sort-by on-sort-by
                                                            :active-sort-by active-sort-by
                                                            :stage active-stage
                                                            :ideabox ideabox
                                                            :icon-only? true}]))
                                         :active-variant active-variant
                                         :on-edit-columns on-edit-columns
                                         :list-variants list-variants
                                         :on-active-variant on-active-variant
                                         :ref ref}
        (when-not mobile
          (let [filter-pills (ideas-filters/idea-filter-pills custom-filters
                                                              (rr/deref* filter-pills-data*)
                                                              (fn [filters]
                                                                (on-change-filters filters)
                                                                (fetch-ideas!))
                                                              (fn [{:ideas.filter/keys [dimension attr]}]
                                                                (set-filter-open [dimension attr])))
                master-form (when-not active-stage (:masterSubmissionForm ideabox))
                field-options (sort-field-options {:stage active-stage :master-form master-form :ideabox ideabox})
                ;; Don't show sort pill if it matches the contextual default
                contextual-default (if active-stage default-sorting default-sorting-all-stages)
                show-sort-pill? (and active-sort-by (not (sort-is-default? active-sort-by active-stage)))
                sort-label (when show-sort-pill? (get-sort-label active-sort-by field-options))
                sort-pill (when sort-label
                            (hx/f [lists-shared/SortPill {:label sort-label
                                                          :order (:order active-sort-by)
                                                          ;; While searching, clearing the sort
                                                          ;; means relevance, not the default sort
                                                          :on-clear #(on-sort-by (when-not searching? contextual-default))}]))]
            (when (or sort-pill (seq filter-pills))
              [lists-shared/FilterPills {:sort-pill sort-pill
                                         :pills filter-pills}])))])]))

(defnc DefaultEmptyState [{:keys [description]}]
  (let [custom-filters (use-ideas-store :custom-filters)
        search-string (use-ideas-store :search-string)]
    (if (or (not (string/blank? search-string))
            (seq custom-filters))
      [empty-states/EmptyState {:heading (t/t "No ideas found")
                                :description (t/t "Check spelling and adjust your search or filter.")
                                :img-src empty-states/no-ideas-found}]
      [empty-states/EmptyState {:heading (t/t "No ideas yet")
                                :description (or description (t/t "Once you create an idea it will show up here."))
                                :img-src empty-states/no-ideas-found}])))

(defnc ^:private Root* [{:keys [user
                                draft-mode ;; boolean - changes the behaviour if this is showing draft ideas only
                                ideas* ;; suspending value with ideas
                                base-filters ;; the baseline filters (e.g. ideabox id, stage id, that is not editable by the user)
                                custom-filters ;; the currently active user-managed filters
                                on-change-filters ;; callback with the new user-managed filters, doesn't fetch ideas
                                title
                                pre-header ;; component to show above the header
                                my-ideas-tabs
                                empty-state
                                ideabox
                                view-state-api
                                on-close-idea-modal
                                active-stage-id
                                on-stage-change
                                ;; optional
                                idea-card-options]}]
  (when ideabox
    ;; when we have an ideabox, we always have an active stage,
    ;; which could be either an actual stage or
    ;; the special form ::all
    (assert (some? active-stage-id)))
  (let [[search-ideas-cached* loading?] (rr/useCachedSuspending ideas*)
        {:keys [totalCount pageInfo nodes]} (rr/deref* search-ideas-cached*)
        [filter-open set-filter-open] (hooks/useState false)
        [edit-columns set-edit-columns] (hooks/useState false)
        mobile (util/use-is-mobile?)
        tablet (util/use-is-tablet?)
        desktop (and (not mobile) (not tablet))
        fetch! (use-get-fetch! nil)
        do-search (use-get-fetch! 500)
        active-stage (when active-stage-id
                       (first (filter #(= (:id %) active-stage-id) (:stages ideabox))))
        filter-menu-inner (hx/f [ideas-filters/IdeaFilterSectionsManager
                                 {:user user
                                  :open filter-open
                                  :base-filters base-filters
                                  :custom-filters custom-filters
                                  :fetch-ideas! fetch!
                                  :on-change-filters on-change-filters
                                  :on-close #(set-filter-open false)
                                  :ideabox-stages (:stages ideabox)}])
        [header-height set-header-height] (hooks/useState 0)

        saved-views (use-ideas-store :saved-views)
        active-view (use-ideas-store :active-view)

        [showing-delete-drafts set-showing-delete-drafts] (hooks/useState false)
        on-change-view (fn [x]
                         (list-protocols/set-view view-state-api x {}))

        on-save-view (fn [view]
                       (list-protocols/save-view view-state-api view))
        on-save-as-view (fn [x]
                          (list-protocols/save-view-as view-state-api x))
        on-delete-view (fn [view]
                         (list-protocols/delete-view view-state-api view))

        can-save-views (list-protocols/can-save-public view-state-api)]

    [:<>
     (when-not draft-mode
       (if mobile
         [modals3/MobileModal
          {:open filter-open
           :on-close #(set-filter-open false)}
          filter-menu-inner]
         [layouts/SidebarDrawerPortal {:open filter-open}
          filter-menu-inner]))
     [:div {:style {:position "relative"
                    :--table-sticky-top (str header-height "px")}}
      (when draft-mode
        [DeleteAllDraftsConfirmationModal {:showing showing-delete-drafts
                                           :on-close #(set-showing-delete-drafts false)
                                           :ideas-count totalCount
                                           :on-success fetch!}])
      [ListHeader {:ref (hooks/useCallback (fn [node]
                                             (when node
                                               (set-header-height (.-offsetHeight node))))
                                           [set-header-height])
                   :set-showing-delete-drafts set-showing-delete-drafts
                   :views-layouts-active (list-protocols/views-layouts-active? view-state-api)
                   :pre-header pre-header
                   :my-ideas-tabs my-ideas-tabs
                   :title title
                   :set-filter-open set-filter-open
                   :draft-mode draft-mode
                   :count totalCount
                   :on-edit-columns #(set-edit-columns true)
                   :active-variant (use-active-variant)
                   :on-active-variant (fn [x]
                                        (list-protocols/set-layout view-state-api x))
                   :active-stage active-stage
                   :active-sort-by (use-ideas-store :orderBy)
                   :on-sort-by (fn [order-by]
                                 (list-protocols/set-order-by view-state-api order-by)
                                 (fetch!))
                   :loading loading?
                   :filter-active (or filter-open (seq custom-filters))
                   :custom-filters custom-filters
                   :on-search (fn [s]
                                (list-protocols/set-search view-state-api s)
                                (do-search))
                   :mobile mobile
                   :list-variants (cond
                                    tablet [:list :masonry]
                                    desktop [:list :masonry :table])
                   :on-change-filters on-change-filters
                   :ideabox ideabox
                   :active-stage-id active-stage-id
                   :on-stage-change on-stage-change
                   :fetch-ideas! fetch!

                   :saved-views saved-views
                   :active-view active-view
                   :on-change-view on-change-view

                   :on-save-view (when can-save-views on-save-view)
                   :on-save-as-view (when can-save-views on-save-as-view)
                   :on-delete-view (when can-save-views on-delete-view)}]

      [ideas-table/EditColumnsModal {:set-open set-edit-columns
                                     :open edit-columns
                                     :key (:id active-stage)
                                     :master-form (:masterSubmissionForm ideabox)
                                     :stage active-stage
                                     :set-selected-columns (fn [cs]
                                                             (list-protocols/set-selected-fields view-state-api cs)
                                                             (set-edit-columns false)
                                                             (fetch!))
                                     :selected-columns (use-ideas-store :selected-columns)}]
      [idea-card/IdeaCardsContainer
       {:ideas nodes
        :stage active-stage
        :ideabox ideabox
        :total-count totalCount
        :draft-mode draft-mode
        :empty-state (if (React/isValidElement empty-state)
                       empty-state
                       [DefaultEmptyState])
        :loading loading?
        :idea-card-options idea-card-options
        :selected-columns (use-ideas-store :selected-columns)
        :variant (use-active-variant)
        :on-show-more (when (:hasNextPage pageInfo)
                        (fn []
                          (list-protocols/show-more view-state-api)
                          (fetch!)))
        :on-close-modal
        (fn []
          (fetch!)
          (when on-close-idea-modal
            (on-close-idea-modal)))}]]]))

(defn receive-reload-message [fetch! e]
  (let [data (.-data e)
        [cmd id] (string/split data #":")]
    (case cmd
      "load" (fetch!)
      nil)))

(defnc IdeaListReloader []
  (let [fetch! (use-get-fetch! nil)
        listener (hooks/useCallback (partial receive-reload-message fetch!) [fetch!])]
    (hooks/useEffect
     (fn []
       (.addEventListener js/window "message" listener)
       #(.removeEventListener js/window "message" listener))
     [listener])))

(defn use-get-state []
  (let [{:keys [state]} (hooks/useContext IdeaListStoreContext)]
    state))

(defnc Root
  [{:keys [user
           draft-mode
           idea-card-options
           title
           pre-header
           my-ideas-tabs
           empty-state
           ideabox
           on-close-idea-modal
           view-state-api
           active-stage-id
           on-stage-change
           on-filters-change]
    :or {idea-card-options {:in :ideabox}}}]
  (let [search-ideas* (use-ideas-store :search-ideas*)
        custom-filters (use-ideas-store :custom-filters)
        state (use-get-state)]

    ;; Update filter-pills-data* when custom-filters changes
    ;; This is not ideal since it updates the filter-pills-data* only after a react render
    ;; but we have to consider that custom-filters can be updated by our event handlers AND
    ;; by a change in the browser history
    (hooks/useEffect
     (fn []
       (swap! state assoc :filter-pills-data*
              (when (seq custom-filters)
                (rr/suspending-value (ideas-filters/fetch-filter-pills-data custom-filters (:id ideabox))))))
     [custom-filters])
    [React/Suspense {:fallback (hx/f [loading/PageLoading])}
     [Root* {:user user
             :ideas* search-ideas*
             :title title
             :pre-header pre-header
             :my-ideas-tabs my-ideas-tabs
             :empty-state empty-state
             :draft-mode draft-mode
             :base-filters (use-ideas-store :search)
             :custom-filters custom-filters
             :ideabox ideabox
             :on-close-idea-modal on-close-idea-modal
             :active-stage-id active-stage-id
             :on-stage-change on-stage-change
             :view-state-api view-state-api
             :on-change-filters (fn [new-filters]
                                  (list-protocols/set-filters view-state-api new-filters)
                                  (when on-filters-change
                                    (on-filters-change new-filters)))
             :idea-card-options idea-card-options}]]))
